import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Brain, AlertCircle, CheckCircle, Clock } from 'lucide-react';

interface ReasoningData {
  understanding?: {
    original_query: string;
    intent: string;
    entities: Record<string, any>;
    workflow_stage: string;
    confidence: number;
    reasoning?: string;
  };
  tool_selection?: Array<{
    tool: string;
    reason: string;
    alternatives_considered?: Array<{
      tool_name: string;
      why_rejected: string;
    }>;
    preconditions?: {
      met: string[];
      failed: string[];
    };
    estimated_time_ms?: number;
    blocked?: boolean;
    blocking_reason?: string;
  }>;
  execution_plan?: {
    steps: Array<{
      tool_name: string;
      description: string;
      estimated_ms: number;
      is_dependency: boolean;
    }>;
    total_time_ms: number;
    blockers?: string[];
    warnings?: string[];
    can_execute: boolean;
  };
  steps?: Array<{
    step_type: string;
    content: string;
    timestamp: string;
    confidence?: number;
  }>;
}

interface ReasoningPanelProps {
  reasoning: ReasoningData;
  isExpanded?: boolean;
  onToggle?: () => void;
}

export const ReasoningPanel: React.FC<ReasoningPanelProps> = ({
  reasoning,
  isExpanded: externalIsExpanded,
  onToggle
}) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalIsExpanded(!internalIsExpanded);
    }
  };

  if (!reasoning) return null;

  return (
    <div className="border border-[#3e3e42] rounded-lg bg-[#252526] overflow-hidden">
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-[#2d2d30] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium text-gray-200">Reasoning Transparency</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-[#3e3e42]">
          {/* Understanding Section */}
          {reasoning.understanding && (
            <Section title="Understanding" icon={<Brain className="w-4 h-4 text-blue-400" />}>
              <div className="space-y-2">
                <InfoRow label="Query" value={reasoning.understanding.original_query} />
                <InfoRow label="Intent" value={reasoning.understanding.intent} badge />
                <InfoRow label="Workflow Stage" value={reasoning.understanding.workflow_stage} badge />
                <InfoRow
                  label="Confidence"
                  value={`${Math.round(reasoning.understanding.confidence * 100)}%`}
                  progressBar
                  progressValue={reasoning.understanding.confidence}
                />

                {reasoning.understanding.entities && Object.keys(reasoning.understanding.entities).length > 0 && (
                  <div className="mt-2">
                    <span className="text-xs font-medium text-gray-400">Extracted Entities:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {Object.entries(reasoning.understanding.entities).map(([key, value]) => (
                        <span
                          key={key}
                          className="text-xs bg-[#1e1e1e] text-blue-300 px-2 py-1 rounded border border-blue-500/30"
                        >
                          {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {reasoning.understanding.reasoning && (
                  <div className="mt-2 text-xs text-gray-300 italic">
                    {reasoning.understanding.reasoning}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Tool Selection Section */}
          {reasoning.tool_selection && reasoning.tool_selection.length > 0 && (
            <Section title="Tool Selection" icon={<CheckCircle className="w-4 h-4 text-green-400" />}>
              <div className="space-y-3">
                {reasoning.tool_selection.map((selection, idx) => (
                  <div key={idx} className="bg-[#1e1e1e] rounded p-3 border border-[#3e3e42]">
                    {/* Tool Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {selection.blocked ? (
                          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        )}
                        <code className="text-sm font-mono text-blue-300">{selection.tool}</code>
                      </div>
                      {selection.estimated_time_ms && (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          ~{(selection.estimated_time_ms / 1000).toFixed(1)}s
                        </div>
                      )}
                    </div>

                    {/* Reason */}
                    <p className="text-xs text-gray-300 mb-2">{selection.reason}</p>

                    {/* Blocking Reason */}
                    {selection.blocked && selection.blocking_reason && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded p-2 mb-2">
                        <p className="text-xs text-red-300">{selection.blocking_reason}</p>
                      </div>
                    )}

                    {/* Preconditions */}
                    {selection.preconditions && (
                      <div className="flex gap-4 text-xs">
                        {selection.preconditions.met && selection.preconditions.met.length > 0 && (
                          <div>
                            <span className="text-gray-400">✓ Met: </span>
                            <span className="text-green-300">{selection.preconditions.met.join(', ')}</span>
                          </div>
                        )}
                        {selection.preconditions.failed && selection.preconditions.failed.length > 0 && (
                          <div>
                            <span className="text-gray-400">✗ Failed: </span>
                            <span className="text-red-300">{selection.preconditions.failed.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Alternatives */}
                    {selection.alternatives_considered && selection.alternatives_considered.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#3e3e42]">
                        <span className="text-xs font-medium text-gray-400">Alternatives Considered:</span>
                        <div className="mt-1 space-y-1">
                          {selection.alternatives_considered.map((alt, altIdx) => (
                            <div key={altIdx} className="text-xs text-gray-400">
                              <code className="text-yellow-400">{alt.tool_name}</code>: {alt.why_rejected}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Execution Plan Section */}
          {reasoning.execution_plan && (
            <Section title="Execution Plan" icon={<Clock className="w-4 h-4 text-orange-400" />}>
              <div className="space-y-3">
                {/* Blockers */}
                {reasoning.execution_plan.blockers && reasoning.execution_plan.blockers.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-300">Blockers</span>
                    </div>
                    <ul className="space-y-1">
                      {reasoning.execution_plan.blockers.map((blocker, idx) => (
                        <li key={idx} className="text-xs text-red-300">• {blocker}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {reasoning.execution_plan.warnings && reasoning.execution_plan.warnings.length > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-medium text-yellow-300">Warnings</span>
                    </div>
                    <ul className="space-y-1">
                      {reasoning.execution_plan.warnings.map((warning, idx) => (
                        <li key={idx} className="text-xs text-yellow-300">• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Steps */}
                {reasoning.execution_plan.steps && reasoning.execution_plan.steps.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-xs font-medium text-gray-400">Steps:</span>
                    {reasoning.execution_plan.steps.map((step, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 bg-[#1e1e1e] rounded p-2 border border-[#3e3e42]"
                      >
                        <span className="text-xs font-mono text-gray-400 flex-shrink-0">
                          {idx + 1}.
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-blue-300">{step.tool_name}</code>
                            {step.is_dependency && (
                              <span className="text-xs text-purple-400">(dependency)</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{step.description}</p>
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          ~{(step.estimated_ms / 1000).toFixed(1)}s
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Total Time */}
                {reasoning.execution_plan.total_time_ms !== undefined && (
                  <div className="pt-2 border-t border-[#3e3e42]">
                    <span className="text-xs text-gray-400">Total Estimated Time: </span>
                    <span className="text-sm font-medium text-orange-300">
                      ~{(reasoning.execution_plan.total_time_ms / 1000).toFixed(1)} seconds
                    </span>
                  </div>
                )}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

// Helper Components

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, icon, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
    </div>
    <div className="ml-6">
      {children}
    </div>
  </div>
);

interface InfoRowProps {
  label: string;
  value: string;
  badge?: boolean;
  progressBar?: boolean;
  progressValue?: number;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, badge, progressBar, progressValue }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-gray-400 min-w-[100px]">{label}:</span>
    {badge ? (
      <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded border border-blue-500/30">
        {value}
      </span>
    ) : progressBar && progressValue !== undefined ? (
      <div className="flex items-center gap-2 flex-1">
        <div className="flex-1 h-2 bg-[#1e1e1e] rounded-full overflow-hidden border border-[#3e3e42]">
          <div
            className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-300"
            style={{ width: `${progressValue * 100}%` }}
          />
        </div>
        <span className="text-xs text-gray-300 min-w-[35px]">{value}</span>
      </div>
    ) : (
      <span className="text-xs text-gray-300">{value}</span>
    )}
  </div>
);

export default ReasoningPanel;
